/**
 * GovernmentPersonDialog — Government-group entry point for the RAO people form.
 *
 * Composes the Government section suite on top of the shared modal shell.
 *
 * HEADS UP — this is a SKELETON per the discovery agent's report. The §16
 * Government walkthroughs were NOT written in Bill's design doc. PublicFunds
 * is the primary/clearest path; Donation + ICS_Personal are best-effort
 * per government.md. See TODO(bill) comments for walkthrough-pending items.
 *
 * Architecture notes:
 *   - `useGovernmentDerivation` derives callClass + tinCode + cddExempt from
 *     the officer's sub-type pick; Identity section publishes the derived
 *     value back into local state via onChange so the cart envelope + save
 *     dispatch see the authoritative values.
 *   - `customerType` is manually selected via the Sub-type dropdown (unlike
 *     Individual which auto-derives from DOB / citizenship).
 *   - OMITS: Citizenship section, Employment section, CIP scan panel — a
 *     government entity has no photo ID; authorized finance officers have
 *     their own Individual CIF records.
 *   - attachedDocs lifted here; passed down to CIP section (uploads for
 *     Finance Officer Authorization + Governing Body Resolution) and to
 *     DocumentSlotsSection (full catalog-driven grid).
 *   - supports readOnly mode mirroring Individual; always-modal surface.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import type { RaoPerson } from '../../../types';
import { GovernmentIdentitySection } from './GovernmentIdentitySection';
import { GovernmentCipSection } from './GovernmentCipSection';
import { useGovernmentDerivation } from './useGovernmentDerivation';

// Shared sections
import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { DocumentSlotsSection } from '../shared/DocumentSlotsSection';
import { PdwFooterDyad } from '../shared/PdwFooterDyad';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import { EntityConvergedShell } from '../EntityConvergedShell';
import type { PdwTabKey } from '../PdwTabStrip';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export type GovernmentPersonDialogMode = 'review-add' | 'new' | 'edit';

export interface GovernmentPersonDialogProps {
  open: boolean;
  mode: GovernmentPersonDialogMode;
  person: RaoPerson;
  onSave: (person: RaoPerson) => void;
  onClose: () => void;
  cartCifIds?: ReadonlyArray<string>;
  /** Required by DocumentSlotsSection for per-cart document attachment. */
  cartId: string;
  /** Required by DocumentSlotsSection for upload metadata. Defaults to 'UNKNOWN'. */
  officerCode?: string;
  /** When true, disables every input + hides edit/save controls. */
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function GovernmentPersonDialog({
  open,
  mode,
  person,
  onSave,
  onClose,
  cartCifIds,
  cartId,
  officerCode,
  readOnly = false,
}: GovernmentPersonDialogProps): JSX.Element | null {
  const [local, setLocal] = useState<RaoPerson>(person);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // Slot-4 T0 (Contract C2) — attachedDocs live on the REAL draft
  // (local.extra.attachedDocs). Producers: GovernmentCipSection (Finance
  // Officer Auth + Governing Body Resolution) + DocumentSlotsSection.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode doc hydration from the v3 Document Library.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  const sectionMode: 'new' | 'existing' = mode === 'new' ? 'new' : 'existing';
  const derivation = useGovernmentDerivation(local, {
    mode: sectionMode,
    storedType: person.customerType,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...patch };
      // Keep fullName display in sync for downstream cart rendering.
      // Government uses Entity Name (firstName) + optional Dept (name2).
      const fn = next.firstName ?? '';
      const ln = next.lastName ?? '';
      const n2 = next.name2 ?? '';
      const display = fn || [fn, ln].filter((x) => !!x).join(' ').trim() || n2;
      if (display) next.fullName = display;
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  const isChanged = useCallback(
    (field: string): boolean => {
      // Support nested extra.* keys for government-specific fields.
      if (field.startsWith('extra.')) {
        const key = field.slice('extra.'.length);
        const a = (local.extra ?? {})[key];
        const b = (person.extra ?? {})[key];
        return a !== b && !(a == null && b == null);
      }
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  const displayName =
    local.firstName || local.name2
      ? [local.firstName, local.name2].filter(Boolean).join(' — ')
      : 'Government Entity';

  const title =
    mode === 'review-add'
      ? `Review & add — ${displayName}`
      : mode === 'new'
        ? 'Add government entity'
        : `Edit entity — ${displayName}`;

  const btnLabel =
    mode === 'review-add'
      ? hasChanges ? 'Add existing w/ changes' : 'Add existing'
      : mode === 'new'
        ? 'Add new'
        : 'Save changes';

  // Save-disable surfaces:
  //   - duplicate CIF already on cart
  //   - ICS_Personal Finance-only block
  //   - sub-type-level supervisor approval (ICS_Personal)
  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  const saveDisabled =
    dupInCart ||
    derivation.financeOnly ||
    derivation.requiresSupervisorApproval;

  // BACKLOG-0016 — sticky-footer Return/Continue dyad (flat-scroll dialog).
  const continueDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : derivation.financeOnly
      ? 'ICS_Personal is finance-only — Save blocked.'
      : derivation.requiresSupervisorApproval
        ? 'Supervisor approval required before saving.'
        : undefined;

  // ---------------------------------------------------------------------------
  // Section tree
  //
  // OMITTED (vs Individual):
  //   - IndividualCipScanSection (no photo ID for government entity)
  //   - IndividualEmploymentSection
  //   - IndividualCitizenshipSection
  // ---------------------------------------------------------------------------
  const sectionsByTab: Partial<Record<PdwTabKey, JSX.Element>> = {
    profile: (
      <GovernmentIdentitySection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        derivation={derivation}
        readOnly={readOnly}
      />
    ),
    cip: (
      <GovernmentCipSection
        person={local}
        onChange={update}
        mode={sectionMode}
        isChanged={isChanged}
        governmentSubType={derivation.governmentSubType}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
    contact: (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
        <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
      </div>
    ),
    documents: (
      <DocumentSlotsSection
        person={local}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        entityType={derivation.customerType}
        attachedDocs={attachedDocs}
        onDocumentRegistered={registerAttachedDoc}
        readOnly={readOnly}
      />
    ),
  };

  // Modal surface — every entry into the dialog (New Acct + Maint CIF).
  return (
    <ModalShell open={open} onClose={onClose} title={title} size="lg">
      <EntityConvergedShell
        person={local}
        mode={mode}
        dupInCart={dupInCart}
        tabs={['review', 'profile', 'cip', 'contact', 'documents']}
        sectionsByTab={sectionsByTab}
        onAttachedDocsChange={(docs) => docs.forEach(registerAttachedDoc)}
        onPersonPatch={update}
        cartId={cartId}
        officerCode={officerCode ?? 'UNKNOWN'}
        readOnly={readOnly}
      />

      <PdwFooterDyad
        currentStep={0}
        totalSteps={1}
        onReturn={onClose}
        onContinue={() => onSave(local)}
        continueDisabled={saveDisabled || readOnly}
        continueDisabledReason={continueDisabledReason}
        lastStepLabel={`${btnLabel} →`}
      />
    </ModalShell>
  );
}
